import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useRouter } from '~/hooks/useRouter'
import { useWebchat } from '~/components/Webchat/useWebchat'
import { stripLocale } from '~/components/Webchat/stripLocale'
import { buildViewMessages } from '~/components/Webchat/buildViewMessages'
import { WebChat } from '~/components/Webchat/ui/WebChat'
import { ContactBar } from '~/components/Webchat/ui/ContactBar'
import type { WebChatLabels } from '~/components/Webchat/ui/types'

// Proactive-engagement timing + per-session guard key.
const NUDGE_DELAY_MS = 60 * 1000
const NUDGE_KEY = 'webchat_nudged'

export const FloatingMessenger: React.FC = () => {
  const router = useRouter()
  const intl = useIntl()
  const t = (id: string) => intl.formatMessage({ id })

  // Backend wants 'ru' | 'ro' lowercase + the locale-stripped page path.
  const locale = intl.locale === 'ru' ? 'ru' : 'ro'
  const path = stripLocale(router.pathname)
  const chat = useWebchat({ locale, path })
  const [contactSent, setContactSent] = useState(false)

  // Proactive CTA: after 1 min on the site, alert the admin (Telegram) that the
  // visitor is lingering, and drop an operator "need help?" message that
  // auto-opens the chat. Once per session, and never if the user already opened
  // the chat themselves.
  const { panelOpen, reportToAdmin, injectAdminMessage } = chat
  const proactivePrompt = t('webchat.proactivePrompt')
  useEffect(() => {
    if (panelOpen) return
    let already = false
    try {
      already = sessionStorage.getItem(NUDGE_KEY) === '1'
    } catch {
      /* ignore */
    }
    if (already) return
    const id = window.setTimeout(() => {
      try {
        sessionStorage.setItem(NUDGE_KEY, '1')
      } catch {
        /* ignore */
      }
      reportToAdmin(`🔔 Клиент на сайте больше 1 минуты: ${path || '/'}`)
      injectAdminMessage(proactivePrompt)
    }, NUDGE_DELAY_MS)
    return () => window.clearTimeout(id)
  }, [panelOpen, reportToAdmin, injectAdminMessage, proactivePrompt, path])

  // Only surface the chat once the socket is actually connected.
  if (chat.connection !== 'connected') return null

  const isConfiguratorPage = router.asPath.includes('/configurator')

  const dayKey = (iso: string) => {
    const d = new Date(iso)
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
  }
  const todayKey = dayKey(new Date().toISOString())
  const messages = buildViewMessages(chat.messages, {
    time: (iso) =>
      new Date(iso).toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
      }),
    dayKey,
    dayLabel: (iso) =>
      dayKey(iso) === todayKey
        ? t('webchat.today')
        : new Date(iso).toLocaleDateString(locale, {
            day: 'numeric',
            month: 'long',
          }),
  })

  const labels: WebChatLabels = {
    launcherOpen: t('webchat.launcherOpen'),
    launcherClose: t('webchat.launcherClose'),
    close: t('webchat.close'),
    placeholder: t('webchat.placeholder'),
    send: t('webchat.send'),
    statusOnline: t('webchat.status.online'),
    statusAway: t('webchat.status.online'),
    statusOffline: t('webchat.status.online'),
    receiptSending: t('webchat.receipt.sending'),
    receiptSent: t('webchat.receipt.sent'),
    receiptRead: t('webchat.receipt.read'),
    receiptFailed: t('webchat.failed'),
    senderOperator: t('webchat.sender.operator'),
    senderYou: t('webchat.sender.you'),
  }

  const role = t('webchat.role')
  const closed = chat.sessionState === 'CLOSED'
  const notice = chat.terminalError
    ? t('webchat.error')
    : closed
      ? t('webchat.closed')
      : undefined
  const showContact = !notice && !contactSent

  return (
    <WebChat
      rootClassName={isConfiguratorPage ? 'wc-root--configurator' : undefined}
      operator={{ name: 'Elizabet', role }}
      status="online"
      statusLabel={`${labels.statusOnline} · ${role}`}
      messages={messages}
      labels={labels}
      typingLabel={t('webchat.typing')}
      greeting={t('webchat.greeting')}
      unreadCount={chat.unreadCount}
      notice={notice}
      composerDisabled={!!notice}
      open={chat.panelOpen}
      onOpenChange={(o) => (o ? chat.openPanel() : chat.closePanel())}
      onSend={chat.send}
      aboveComposer={
        showContact ? (
          <ContactBar
            prompt={t('webchat.contactPrompt')}
            namePlaceholder={t('webchat.contactName')}
            phonePlaceholder={t('webchat.contactPhone')}
            saveLabel={t('webchat.contactSave')}
            onSubmit={(payload) => {
              chat.identify(payload)
              setContactSent(true)
            }}
          />
        ) : undefined
      }
    />
  )
}
